import { useState } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";

import type { CloudRuntimeView } from "./DiagnosticsCloudRuntimes.logic";
import {
  type CodexReadinessBlockCode,
  type CodexRuntimeReadinessFailureDetails,
} from "./codexRuntimeReadiness";
import { useCodexRuntimeReadiness } from "./useCodexRuntimeReadiness";

export function useCloudRuntimeDiagnostics(runtimes: ReadonlyArray<CloudRuntimeView>): {
  readonly selectedEnvironmentId: EnvironmentId | null;
  readonly selectedRuntime: CloudRuntimeView | null;
  readonly selectEnvironment: (environmentId: EnvironmentId | null) => void;
  readonly readiness: ReturnType<typeof useCodexRuntimeReadiness>["readiness"];
  readonly readinessEvent: {
    readonly label: string;
    readonly runtimeId: string;
    readonly code: CodexReadinessBlockCode;
    readonly details: CodexRuntimeReadinessFailureDetails | null;
  } | null;
  readonly recheck: () => void;
  readonly isChecking: boolean;
} {
  const [pickedId, setPickedId] = useState<EnvironmentId | "auto">("auto");
  const [pinnedRuntime, setPinnedRuntime] = useState<CloudRuntimeView | null>(null);
  const selectedEnvironmentId =
    pickedId === "auto" ? (runtimes[0]?.environmentId ?? null) : pickedId;
  const catalogRuntime =
    selectedEnvironmentId === null
      ? null
      : (runtimes.find((runtime) => runtime.environmentId === selectedEnvironmentId) ?? null);
  const selectedRuntime =
    catalogRuntime ??
    (pickedId !== "auto" && pinnedRuntime?.environmentId === pickedId ? pinnedRuntime : null);
  const readiness = useCodexRuntimeReadiness(
    selectedRuntime
      ? {
          runtimeId: selectedRuntime.runtimeId,
          organizationId: selectedRuntime.cmmdOrganizationId ?? "",
        }
      : null,
  );
  const readinessEvent =
    selectedRuntime && readiness.readiness.status === "blocked"
      ? {
          label: selectedRuntime.label,
          runtimeId: selectedRuntime.runtimeId,
          code: readiness.readiness.code,
          details: readiness.readiness.details ?? null,
        }
      : null;

  return {
    selectedEnvironmentId,
    selectedRuntime,
    readinessEvent,
    selectEnvironment: (environmentId) => {
      if (!environmentId) {
        setPickedId("auto");
        setPinnedRuntime(null);
        return;
      }
      setPickedId(environmentId);
      setPinnedRuntime(runtimes.find((runtime) => runtime.environmentId === environmentId) ?? null);
    },
    ...readiness,
  };
}
